import { useState } from "react";
import { BASE_URL } from "../../../src/components/config/config";
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  TextField,
  Alert,
  InputAdornment,
  Divider,
  Fade,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setLoginDetails } from "../../store/ClientStore/action";

const styles = {
  root: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#f5f7fa",
    padding: 2,
  },
  card: {
    width: "100%",
    maxWidth: 450,
    borderRadius: 3,
    boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
    overflow: "hidden",
  },
  header: {
    background: "linear-gradient(to bottom, #0d9488, #0f766e)",
    color: "white",
    padding: 3,
    textAlign: "center",
  },
  content: {
    padding: 4,
  },
  input: {
    marginBottom: 3,
  },
  button: {
    height: 48,
    marginTop: 2,
    background: "linear-gradient(to bottom, #0d9488, #0f766e)",
    textTransform: "none",
    fontWeight: "bold",
  },
  divider: {
    margin: "24px 0",
    color: "text.secondary",
    "&::before, &::after": {
      borderColor: "divider",
    },
  },
  footer: {
    padding: 2,
    textAlign: "center",
    backgroundColor: "background.paper",
  },
};

export default function ClientLogin() {
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [mobileNumber, setMobileNumber] = useState("");
  const [otpEntered, setOtpEntered] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleOnChangeMobile = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    setMobileNumber(value);
  };

  const handleOnChangeOtp = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 4);
    setOtpEntered(value);
  };

  const handleSendOtp = async () => {
    if (!mobileNumber) {
      setError("Please enter mobile number");
      return;
    }

    if (mobileNumber.length !== 10) {
      setError("Mobile number must be 10 digits");
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${BASE_URL}client/sent-otp?action=login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          country_code: "+91",
          mobile_number: mobileNumber,
        }),
      });

      const data = await response.json();

      if (data.success === 1) {
        setIsOtpSent(true);
        setSuccess(data.message || "OTP sent successfully to your mobile");
      } else {
        setError(data.message || "Failed to send OTP");
      }
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!otpEntered) {
      setError("Please enter OTP");
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${BASE_URL}client/login-with-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          country_code: "+91",
          mobile_number: mobileNumber,
          otp: otpEntered,
        }),
      });

      const data = await response.json();

      if (data.success === 1) {
        dispatch(
          setLoginDetails({
            clientDetails: data?.data?.clientDetails,
            userTokens: { accessToken: data?.data?.token },
            isClientLoggedIn: true,
          })
        );

        setSuccess(data.message || "Logged in successfully");

        setTimeout(() => {
          navigate("/client-profile");
        });
      } else {
        setError(data.message || "Invalid OTP");
      }
    } catch (err) {
      setError("Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseAlert = () => {
    setError(null);
    setSuccess(null);
  };

  return (
    <Box sx={styles.root}>
      <Card sx={styles.card}>
        <Box sx={styles.header}>
          <Typography variant="h5" component="h1" fontWeight="bold">
            Welcome TO The OniT
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
            Sign in to your account
          </Typography>
        </Box>

        <CardContent sx={styles.content}>
          {error && (
            <Alert severity="error" onClose={handleCloseAlert} sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" onClose={handleCloseAlert} sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {!isOtpSent ? (
            <Fade in={!isOtpSent}>
              <Box>
                <TextField
                  fullWidth
                  label="Mobile Number"
                  variant="outlined"
                  value={mobileNumber}
                  onChange={handleOnChangeMobile}
                  sx={styles.input}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">+91</InputAdornment>
                    ),
                  }}
                  disabled={isLoading}
                />

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleSendOtp}
                  disabled={isLoading}
                  sx={styles.button}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Send OTP"
                  )}
                </Button>
              </Box>
            </Fade>
          ) : (
            <Fade in={isOtpSent}>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  OTP sent to +91{mobileNumber}
                </Typography>

                <TextField
                  fullWidth
                  label="Enter OTP"
                  variant="outlined"
                  value={otpEntered}
                  onChange={handleOnChangeOtp}
                  sx={styles.input}
                  disabled={isLoading}
                />

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleLogin}
                  disabled={isLoading}
                  sx={styles.button}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Login"
                  )}
                </Button>

                <Divider sx={styles.divider}>OR</Divider>

                <Button
                  fullWidth
                  variant="text"
                  onClick={() => setIsOtpSent(false)}
                  disabled={isLoading}
                >
                  Use different number
                </Button>
              </Box>
            </Fade>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
